/**
 * WebGPUGpuTimer — per-pass GPU timing via WebGPU timestamp-query.
 *
 * STATUS (2026-07-25, docs/perf/2026-07-25-engine-graphics-findings.md,
 * finding 1b): **`available` is hardcoded `false`.** This backend cannot be
 * made spec-correct without changing `IGpuTimerBackend`. WebGPU only writes
 * timestamps via a pass descriptor's `timestampWrites` field, declared at
 * `beginRenderPass`/`beginComputePass` time — there is no `GPUQueue.write-
 * Timestamp` (never part of the spec; the old code called it as an
 * optional-chained no-op) and no way to attach a timestamp write to an
 * already-open pass. `IGpuTimerBackend.beginPass(name)`/`endPass(name)` are
 * free-standing calls issued around Three.js's opaque `renderer.render()`
 * (see `Renderer.ts#renderFrame`) — this class is never handed a
 * `GPUCommandEncoder` or a pass descriptor, so there is no spec-correct
 * implementation reachable from here. Rather than silently report a
 * device-side no-op (the original two-bug defect: wrong feature-name
 * constant made `available` true on devices that could never actually time
 * anything, PLUS a non-spec `queue.writeTimestamp` that would have resolved
 * every duration to ~0 anyway), this backend now always defers to
 * `NullGpuTimer` via `GpuTimerFactory` and logs why. See the finding for
 * what an `IGpuTimerBackend` change would need to look like to fix this for
 * real.
 *
 * Architecture below (two-frame pipeline to avoid stalls) is retained
 * un-executed — `_isAvailable` is always `false`, so none of it runs — as a
 * reference for a future interface change:
 *
 *  Frame N  : write begin/end timestamps into querySet[N % 2] (would need
 *             pass-level `timestampWrites`, not shown here)
 *             resolveQuerySet → resolveBuffer[N % 2]
 *             copyBufferToBuffer → readbackBuffer[N % 2]
 *             queue.onSubmittedWorkDone → schedule mapAsync for slot N % 2
 *
 *  Frame N+2: readbackBuffer[(N+2-2) % 2] has resolved → mapAsync →
 *             read BigUint64Array → compute durations → store in _resolved
 *
 * The caller would read _resolved synchronously via readFrame().
 * No pipeline stalls — mapAsync is only awaited after onSubmittedWorkDone fires.
 *
 * Limitations:
 *  - maxPasses entries per frame (default 64). Passes beyond maxPasses are
 *    silently dropped to avoid overrunning the query set.
 *  - The feature flag 'timestamp-query' (GPUFeatureName) must be requested
 *    when creating the GPUDevice — see RuntimeCaps.ts. Even when present,
 *    `available` stays false for the structural reason above.
 */
import type { IGpuTimerBackend } from './GpuTimerBackend.js';
import { Logger } from '../../core/Logger.js';

/** Minimal GPUDevice interface — only the fields we need. */
interface GPUDeviceLike {
  features: Set<string>;
  createQuerySet(desc: { type: string; count: number }): GPUQuerySetLike;
  createBuffer(desc: {
    size: number;
    usage: number;
    mappedAtCreation?: boolean;
  }): GPUBufferLike;
  queue: {
    // NOT a real WebGPU API — GPUQueue has no writeTimestamp method in the
    // spec (timestamps are written by the pass encoder via a pass
    // descriptor's `timestampWrites`, not the queue). Kept only because the
    // dead reference implementation below still calls it optionally; see
    // the class doc comment. Never rely on this being defined.
    writeTimestamp?(querySet: GPUQuerySetLike, index: number): void;
    onSubmittedWorkDone(): Promise<void>;
    submit(commandBuffers: GPUCommandBufferLike[]): void;
  };
  createCommandEncoder(): GPUCommandEncoderLike;
}

interface GPUQuerySetLike {
  destroy(): void;
}

interface GPUBufferLike {
  size: number;
  mapAsync(mode: number): Promise<void>;
  getMappedRange(): ArrayBuffer;
  unmap(): void;
  destroy(): void;
}

interface GPUCommandEncoderLike {
  resolveQuerySet(
    querySet: GPUQuerySetLike,
    firstQuery: number,
    queryCount: number,
    destination: GPUBufferLike,
    destinationOffset: number,
  ): void;
  copyBufferToBuffer(
    source: GPUBufferLike,
    sourceOffset: number,
    destination: GPUBufferLike,
    destinationOffset: number,
    size: number,
  ): void;
  finish(): GPUCommandBufferLike;
}

// Opaque GPUCommandBuffer handle returned by encoder.finish(); we only pass it
// back to queue.submit(), so a structural marker keeps it nominal.
type GPUCommandBufferLike = { readonly __gpuCommandBufferBrand: unique symbol } | object;

// GPUMapMode.READ — still used by the (unreachable while _isAvailable is
// false) async readback path in endFrame(), kept because that path is real
// code, just never entered. See the class doc comment.
const GPU_MAP_MODE_READ = 0x0001;

// The WebGPU **feature** name (GPUFeatureName), for device.features.has(...).
// NOT the same string as the GPUQueryType passed to createQuerySet({ type })
// — collapsing those two into one constant was the original bug (finding
// 1b(a)): 'timestamp' is the query type, 'timestamp-query' is the feature.
// createQuerySet is no longer called from this file (see the class doc
// comment), so only the feature-name constant remains.
const FEATURE_TIMESTAMP_QUERY = 'timestamp-query';
const PIPELINE_DEPTH = 2; // frames in-flight before we read back

interface FrameSlot {
  querySet: GPUQuerySetLike;
  /** Intermediate buffer — receives resolveQuerySet output (QUERY_RESOLVE | COPY_SRC) */
  resolveBuffer: GPUBufferLike;
  /** CPU-mappable readback buffer (COPY_DST | MAP_READ) */
  readbackBuffer: GPUBufferLike;
  /** Pass names recorded this frame, in order of beginPass calls. */
  passNames: string[];
  /** How many begin/end pairs were recorded (pair count, not index). */
  pairCount: number;
  /** Whether this slot is pending readback. */
  pending: boolean;
}

export class WebGPUGpuTimer implements IGpuTimerBackend {
  private readonly _device: GPUDeviceLike;
  private readonly _maxPasses: number;
  private readonly _slots: FrameSlot[];
  private _frameIndex = 0;
  private _currentSlot: FrameSlot | null = null;
  private _resolved: Map<string, number> = new Map();
  private _isAvailable: boolean;

  /**
   * @param device   The WebGPU GPUDevice (typed as unknown for compatibility).
   * @param maxPasses Maximum number of pass measurements per frame (default 64).
   */
  constructor(device: unknown, maxPasses = 64) {
    this._device = device as GPUDeviceLike;
    this._maxPasses = maxPasses;

    // `available` is always false — see the class doc comment (finding 1b,
    // route (b)). This is intentional, not a placeholder: even a device that
    // correctly carries the 'timestamp-query' feature cannot be timed by
    // this class, because IGpuTimerBackend's beginPass/endPass shape never
    // hands it a GPUCommandEncoder or pass descriptor to attach
    // `timestampWrites` to. We still check the (correct) feature name so the
    // log message is accurate about *why* — feature absent vs. feature
    // present but unusable through this interface — without ever flipping
    // `_isAvailable` to true.
    const hasFeature = (this._device.features as Set<string>).has(FEATURE_TIMESTAMP_QUERY);
    this._isAvailable = false;
    this._slots = [];

    if (hasFeature) {
      Logger.info(
        'WebGPUGpuTimer',
        'Device has the timestamp-query feature, but WebGPUGpuTimer cannot use it: WebGPU '
        + 'timestamps require pass-creation-time timestampWrites, which IGpuTimerBackend\'s '
        + 'beginPass/endPass shape cannot supply — GPU timing disabled (see finding 1b)',
      );
    } else {
      Logger.info('WebGPUGpuTimer', 'timestamp-query feature not available — GPU timing disabled');
    }
  }

  get available(): boolean {
    return this._isAvailable;
  }

  beginPass(name: string): void {
    if (!this._isAvailable || !this._currentSlot) return;
    const slot = this._currentSlot;
    if (slot.pairCount >= this._maxPasses) return;

    const queryIndex = slot.pairCount * 2; // begin = even index
    slot.passNames.push(name);
    // writeTimestamp is an optional feature on queue in some environments
    this._device.queue.writeTimestamp?.(slot.querySet, queryIndex);
  }

  endPass(name: string): void {
    if (!this._isAvailable || !this._currentSlot) return;
    const slot = this._currentSlot;

    // Guard: only end a pass if there is an unmatched beginPass.
    // passNames grows in beginPass; pairCount increments in endPass.
    // An unmatched begin exists iff passNames.length > pairCount.
    if (slot.passNames.length <= slot.pairCount) return;

    // Optionally verify name matches; we proceed regardless for index alignment
    void name;

    const queryIndex = slot.pairCount * 2 + 1; // end = odd index
    this._device.queue.writeTimestamp?.(slot.querySet, queryIndex);
    slot.pairCount++;
  }

  endFrame(): void {
    if (!this._isAvailable) return;

    const slot = this._currentSlot;
    if (slot && slot.pairCount > 0 && !slot.pending) {
      // Resolve recorded timestamps from querySet → resolveBuffer → readbackBuffer
      const encoder = this._device.createCommandEncoder();
      const resolvedCount = slot.pairCount * 2;

      encoder.resolveQuerySet(slot.querySet, 0, resolvedCount, slot.resolveBuffer, 0);
      encoder.copyBufferToBuffer(
        slot.resolveBuffer, 0,
        slot.readbackBuffer, 0,
        resolvedCount * 8,
      );

      const commands = encoder.finish();
      this._device.queue.submit([commands]);

      slot.pending = true;
      const capturedSlot = slot;
      const capturedNames = slot.passNames.slice();
      const capturedPairCount = slot.pairCount;

      // Schedule async readback — never awaited on the main path
      void this._device.queue.onSubmittedWorkDone().then(async () => {
        if (!capturedSlot.pending) return;
        try {
          await capturedSlot.readbackBuffer.mapAsync(GPU_MAP_MODE_READ);
          const arrayBuffer = capturedSlot.readbackBuffer.getMappedRange();
          const timestamps = new BigUint64Array(arrayBuffer);

          const result = new Map<string, number>();
          for (let i = 0; i < capturedPairCount; i++) {
            const begin = timestamps[i * 2];
            const end = timestamps[i * 2 + 1];
            const passName = capturedNames[i];
            if (passName !== undefined && end >= begin) {
              // Convert BigInt nanoseconds to JS number
              result.set(passName, Number(end - begin));
            }
          }
          this._resolved = result;
          capturedSlot.readbackBuffer.unmap();
        } catch (_err) {
          // Readback failure — leave _resolved as-is from prior frame
        } finally {
          capturedSlot.pending = false;
        }
      });
    }

    // Advance to next slot
    this._frameIndex++;
    const nextSlotIndex = this._frameIndex % PIPELINE_DEPTH;
    const nextSlot = this._slots[nextSlotIndex];

    // Reset next slot only if previous readback is complete (not pending).
    // If it is still pending, its readback is still in flight — it must NOT
    // become _currentSlot (that would let a caller record new begin/end
    // pairs into a slot mapAsync is concurrently reading), so the assignment
    // below is gated on the same condition as the reset, not unconditional.
    if (nextSlot && !nextSlot.pending) {
      nextSlot.passNames.length = 0;
      nextSlot.pairCount = 0;
      this._currentSlot = nextSlot;
    } else {
      this._currentSlot = null;
    }
  }

  /**
   * Begin tracking for a new frame. Must be called once per frame before
   * any beginPass calls. Resets the current slot.
   *
   * Note: This is called by endFrame advancing to the next slot.
   * However, to support external callers that call beginFrame explicitly
   * (e.g., integration into FrameGraph), we expose a separate beginFrame.
   */
  beginFrame(): void {
    if (!this._isAvailable) return;
    const slotIndex = this._frameIndex % PIPELINE_DEPTH;
    const slot = this._slots[slotIndex];
    if (slot && !slot.pending) {
      slot.passNames.length = 0;
      slot.pairCount = 0;
      this._currentSlot = slot;
    } else {
      // Slot still pending from 2 frames ago — skip timing this frame
      this._currentSlot = null;
    }
  }

  readFrame(): ReadonlyMap<string, number> {
    return this._resolved;
  }

  dispose(): void {
    if (!this._isAvailable) return;
    for (const slot of this._slots) {
      slot.querySet.destroy();
      slot.resolveBuffer.destroy();
      slot.readbackBuffer.destroy();
    }
    this._slots.length = 0;
    this._resolved.clear();
  }
}
